import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Table, MapPin, Grid3X3, Filter, X, Star } from "lucide-react";
import DirectoryListView from "@/components/directory/DirectoryListView";
import DirectoryMapView from "@/components/directory/DirectoryMapView";
import DirectoryFilters, { FiltersProps } from "@/components/directory/DirectoryFilters";
import DirectoryTableView from "@/components/directory/DirectoryTableView";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import NoResultsFound from "@/components/directory/NoResultsFound";
import { fetchCounties, CountyOption } from "@/services/locationService";
import { SimpleComingSoon } from "./ComingSoon";
import { isComingSoonEnabled } from "@/utils/featureFlags";
import UnifiedMobileFooter from "@/components/UnifiedMobileFooter";

type ViewMode = "grid" | "table" | "map";

const Directory: React.FC = () => {
  if (isComingSoonEnabled()) return <SimpleComingSoon />;

  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [filters, setFilters] = useState<FiltersProps>(() => {
    // Try to load from localStorage
    const savedFilters = localStorage.getItem('directoryFilters');
    if (savedFilters) {
      try {
        const parsedFilters = JSON.parse(savedFilters);
        return parsedFilters;
      } catch (e) {
        console.error("Failed to parse saved filters", e);
      }
    }
    
    // Default filters if no saved state
    return {
      search: "",
      serviceTypes: [], // Initialize with empty array
      placeTypes: [] as string[],
      experiences: [] as string[],
      amenities: [] as string[],
      selectedCountry: "", // No default country
      selectedCounties: [] as string[],
      showAll: true,
      type: "all",
      location: "",
      showFavoritesOnly: false
    };
  });
  
  // Tracking if there are search results
  const [hasResults, setHasResults] = useState(true);
  const [resultsCount, setResultsCount] = useState(0);
  const [counties, setCounties] = useState<CountyOption[]>([]);
  const [loadingCounties, setLoadingCounties] = useState(false);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('directoryFilters', JSON.stringify(filters));
  }, [filters]);

  // Fetch counties when selectedCountry changes
  useEffect(() => {
    if (!filters.selectedCountry) {
      setCounties([]);
      return;
    }
    setLoadingCounties(true);
    fetchCounties(filters.selectedCountry)
      .then((data) => {
        setCounties(data);
        setLoadingCounties(false);
      })
      .catch(() => {
        setCounties([]);
        setLoadingCounties(false);
      });
  }, [filters.selectedCountry]);

  const handleFilterChange = (filterType: string, value: string | string[] | boolean) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };
  
  const handleViewModeChange = (value: string | undefined) => {
    if (!value) return;
    setViewMode(value as ViewMode);
  };
  
  // Function to handle removing individual filters
  const removeFilter = (type: string, value: string) => {
    if (type === "serviceTypes" && value === "sauna") return; // Prevent removing sauna
    
    setFilters(prev => ({
      ...prev,
      [type]: Array.isArray(prev[type as keyof FiltersProps]) 
        ? (prev[type as keyof FiltersProps] as string[]).filter(item => item !== value)
        : prev[type as keyof FiltersProps]
    }));
  };
  
  // Function to clear all filters except sauna
  const clearAllFilters = () => {
    setFilters({
      ...filters,
      search: "",
      serviceTypes: [], // Initialize with empty array
      placeTypes: [],
      experiences: [],
      amenities: [],
      selectedCounties: [],
      type: "all",
      showFavoritesOnly: false
    });
  };
  
  // Get total selected filters for badge display
  const getTotalSelectedFilters = () => {
    return (
      (filters.serviceTypes?.filter(type => type !== "sauna")?.length || 0) + 
      (filters.placeTypes?.length || 0) + 
      (filters.experiences?.length || 0) + 
      (filters.amenities?.length || 0) +
      (filters.selectedCounties?.length || 0) +
      (filters.search ? 1 : 0) +
      (filters.showFavoritesOnly ? 1 : 0)
    );
  };
  
  // Function to update hasResults state - this would be called by the directory views
  const updateResultsState = (resultsExist: boolean, count?: number) => {
    setHasResults(resultsExist);
    if (count !== undefined) {
      setResultsCount(count);
    }
  };

  // Format the results summary text
  const getResultsSummary = () => {
    if (resultsCount === 0) return "";
    
    const countyText = filters.selectedCounties.length > 0 
      ? `in ${filters.selectedCounties.length} ${filters.selectedCounties.length === 1 ? 'county' : 'counties'}`
      : '';
      
    return `${resultsCount} ${resultsCount === 1 ? 'sauna' : 'saunas'} found ${countyText}`;
  };

  // Function to get icon for filter type
  const getFilterIcon = (type: string) => {
    switch(type) {
      case 'serviceTypes': return 'bg-sauna-orange/10 text-sauna-orange';
      case 'placeTypes': return 'bg-sauna-blue/10 text-sauna-blue';
      case 'experiences': return 'bg-green-100 text-green-800';
      case 'amenities': return 'bg-purple-100 text-purple-800';
      case 'selectedCounties': return 'bg-gray-100 text-gray-800';
      case 'search': return 'bg-sauna-navy/10 text-sauna-navy';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Handler for 'Near My Location' button
  const handleNearMe = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFilters((prev) => ({
          ...prev,
          location: {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            radius: 25 // km, can be made configurable
          },
          selectedCounties: [] // Clear county filter
        }));
      },
      (error) => {
        alert("Unable to retrieve your location.");
      }
    );
  };

  return (
    <>
      <Helmet>
        <title>Directory | SaunaDip</title>
        <meta name="description" content="Find your next Sauna & plunge experience across Ireland & UK" />
      </Helmet>
      
      <div className="min-h-screen bg-white py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
            <div>
              <h1 className="text-3xl font-bold text-sauna-navy">
                Sauna & Plunge Directory
              </h1>
              <p className="text-gray-500 mt-1">
                Find your next Sauna & plunge experience across Ireland & UK
              </p>
              <div className="h-1 w-24 divider-gradient rounded-full mt-2"></div>
            </div>
            
            <div className="flex items-center">
              <ToggleGroup type="single" value={viewMode} onValueChange={handleViewModeChange}>
                <ToggleGroupItem value="table" aria-label="List view" className="bg-sauna-beige data-[state=on]:bg-sauna-orange data-[state=on]:text-white">
                  <Table className="mr-2" />
                  List
                </ToggleGroupItem>
                
                <ToggleGroupItem value="grid" aria-label="Grid view" className="bg-sauna-beige data-[state=on]:bg-sauna-orange data-[state=on]:text-white">
                  <Grid3X3 className="mr-2" />
                  Grid
                </ToggleGroupItem>
                
                <ToggleGroupItem value="map" aria-label="Map view" className="bg-sauna-beige data-[state=on]:bg-sauna-orange data-[state=on]:text-white">
                  <MapPin className="mr-2" />
                  Map
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          </div>
          
          <DirectoryFilters 
            filters={filters} 
            onFilterChange={handleFilterChange}
            onClearAll={clearAllFilters}
            onNearMe={handleNearMe}
          />
          
          {/* Active filters section - updated with icon-based styling */}
          {getTotalSelectedFilters() > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-500">Active filters:</span>
              
              {/* Service Types */}
              {filters.serviceTypes.filter(type => type !== "sauna").map(type => (
                <Badge 
                  key={`service-${type}`} 
                  className={`${getFilterIcon('serviceTypes')} border-none hover:bg-sauna-orange/20 flex items-center gap-1`}
                >
                  {type.replace('-', ' ')}
                  <X 
                    className="ml-1 h-3 w-3 cursor-pointer" 
                    onClick={() => removeFilter('serviceTypes', type)}
                  />
                </Badge>
              ))}
              
              {/* Place Types */}
              {filters.placeTypes.map(type => (
                <Badge 
                  key={`place-${type}`} 
                  className={`${getFilterIcon('placeTypes')} border-none hover:bg-sauna-blue/20 flex items-center gap-1`}
                >
                  {type.replace('-', ' ')}
                  <X 
                    className="ml-1 h-3 w-3 cursor-pointer" 
                    onClick={() => removeFilter('placeTypes', type)}
                  />
                </Badge>
              ))}
              
              {/* Experiences */}
              {filters.experiences.map(exp => (
                <Badge 
                  key={`exp-${exp}`} 
                  className={`${getFilterIcon('experiences')} border-none hover:bg-green-200 flex items-center gap-1`}
                >
                  {exp.replace('-', ' ')}
                  <X 
                    className="ml-1 h-3 w-3 cursor-pointer" 
                    onClick={() => removeFilter('experiences', exp)}
                  />
                </Badge>
              ))}
              
              {/* Amenities */}
              {filters.amenities.map(amenity => (
                <Badge 
                  key={`amenity-${amenity}`} 
                  className={`${getFilterIcon('amenities')} border-none hover:bg-purple-200 flex items-center gap-1`}
                >
                  {amenity.replace('-', ' ')}
                  <X 
                    className="ml-1 h-3 w-3 cursor-pointer" 
                    onClick={() => removeFilter('amenities', amenity)}
                  />
                </Badge>
              ))}
              
              {/* Counties */}
              {filters.selectedCounties.map(countyCode => {
                const countyObj = counties.find(c => c.value === countyCode);
                return (
                  <Badge 
                    key={`county-${countyCode}`} 
                    className={`${getFilterIcon('selectedCounties')} border-none hover:bg-gray-200 flex items-center gap-1`}
                  >
                    {countyObj ? countyObj.label : countyCode}
                    <X 
                      className="ml-1 h-3 w-3 cursor-pointer" 
                      onClick={() => removeFilter('selectedCounties', countyCode)}
                    />
                  </Badge>
                );
              })}
              
              {/* Search term if present */}
              {filters.search && (
                <Badge 
                  className={`${getFilterIcon('search')} border-none hover:bg-sauna-navy/20 flex items-center gap-1`}
                >
                  Search: {filters.search}
                  <X 
                    className="ml-1 h-3 w-3 cursor-pointer" 
                    onClick={() => handleFilterChange('search', '')}
                  />
                </Badge>
              )}
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={clearAllFilters}
                className="ml-2 text-gray-500 border-gray-300 hover:bg-gray-100"
              >
                Clear all
              </Button>

              {/* Results count summary */}
              {hasResults && resultsCount > 0 && (
                <div className="ml-auto px-3 py-1 bg-sauna-blue/10 text-sauna-navy rounded-full text-sm font-medium">
                  {getResultsSummary()}
                </div>
              )}
              
              {/* Favorites Filter Badge */}
              {filters.showFavoritesOnly && (
                <Badge 
                  className="border-none hover:bg-sauna-orange/20 flex items-center gap-1 bg-sauna-orange/10 text-sauna-orange"
                >
                  <Star className="h-3 w-3" />
                  Favorites Only
                  <X 
                    className="ml-1 h-3 w-3 cursor-pointer" 
                    onClick={() => handleFilterChange('showFavoritesOnly', false)}
                  />
                </Badge>
              )}
            </div>
          )}
          
          <div className="mt-6">
            {/* Show NoResultsFound component when there are no results */}
            {!hasResults ? (
              <NoResultsFound onClearFilters={clearAllFilters} />
            ) : viewMode === "table" ? (
              <DirectoryTableView filters={filters} onResultsChange={updateResultsState} />
            ) : viewMode === "map" ? (
              <DirectoryMapView filters={filters} onResultsChange={updateResultsState} />
            ) : (
              <DirectoryListView filters={filters} columns={6} onResultsChange={updateResultsState} />
            )}
          </div>
        </div>
      </div>
      <UnifiedMobileFooter />
    </>
  );
};

export default Directory;
